import React, { useState } from 'react'
import styled, { createGlobalStyle, css } from 'styled-components'
import Link from 'gatsby-link'
import { CSSTransition } from 'react-transition-group'

import { useMedia } from '@/utils/use-media'
import giphyLogoImg from '@/assets/images/header-logo.gif'
import { Button } from '@/components/common/create-app-button'
import { CTAKey, getCTAByKey } from '@/components/common/cta'
import { gaEvents } from '@/utils/ga-events'
import { headerLinks } from '@/config/links'
import { isDocsPage, isLandingPage, useLocationChecker } from '@/utils/location'
import { MenuIcon } from '@/components/ui/icons'
import { useIsMounted } from '@/utils/use-is-mounted'

import ModalNavMenu from './mobile-nav-menu'
import { NavMenuLinkList } from './nav-menu-link-list'
import { desktopViewQuery, mobileViewQuery } from './const'

const NavContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: ${({ theme }) => theme.headerHeight}px;
  color: white;
  box-sizing: border-box;
  margin: 0 auto;
  padding: 0 6px;

  max-width: ${({ theme }) => `${theme.containerWidth}px`};

  ${(props) =>
    !props.theme.disableMobileQueries
      ? css`
          @media ${mobileViewQuery} {
            width: 100vw;
            margin: 0;
            padding: 0 14px;
          }
        `
      : ''}

  img {
    width: 310px;

    ${(props) =>
      !props.theme.disableMobileQueries
        ? css`
            @media ${mobileViewQuery} {
              width: 250px;
            }
          `
        : ''}
  }

  a {
    color: white;
  }
`

const GlobalStyle = createGlobalStyle`
    body {
        overflow: hidden;
    }
`

const ModalMenuContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;

  &.fadeScale.enter {
    opacity: 0.01;
    transform: scale(0.95);
  }

  &.fadeScale.enter-active {
    opacity: 1;
    transform: scale(1);
    transition: opacity 200ms ease-in, transform 200ms ease-in;
  }

  &.fadeScale.exit {
    opacity: 1;
    transform: scale(1);
  }

  &.fadeScale.exit-active {
    opacity: 0.01;
    transform: scale(0.95);
    transition: opacity 200ms ease-in, transform 200ms ease-in;
  }
`

const MenuButton = styled.div`
  width: 30px;
  height: 42px;
  position: relative;
  z-index: 2;
`

const ExtendedLink = styled(Link)`
  border-bottom: none;
  color: white;
`

const CreateAppButton = styled(Button)<{ $docsPageActive: boolean }>`
  border-bottom: none;
  line-height: 36px;
  margin: 0 15px 0 0;
  width: 140px;

  @media (max-width: 1042px) {
    display: ${({ $docsPageActive }) => ($docsPageActive ? undefined : 'none')};
  }
`

type HeaderProps = {
  ctaKey: CTAKey
}

export const Header: React.FC<HeaderProps> = (props) => {
  const { ctaKey } = props
  const CTA = getCTAByKey(ctaKey)
  const [showModalMenu, setShowModalMenu] = useState(false)
  const landingPageActive = useLocationChecker(isLandingPage)
  const docsPageActive = useLocationChecker(isDocsPage)
  const isMounted = useIsMounted()
  const isDesktopScreen = useMedia(desktopViewQuery, true)
  const showDesktopVersion = docsPageActive ? true : isDesktopScreen

  const onMenuClick = () => {
    setShowModalMenu(!showModalMenu)
  }

  return (
    <NavContainer>
      <ExtendedLink to={'/'}>
        <img alt="logo" src={giphyLogoImg} />
      </ExtendedLink>
      {showDesktopVersion
        ? isMounted && (
            <>
              <NavMenuLinkList titles={headerLinks} />
              {!landingPageActive && (
                <CreateAppButton
                  $docsPageActive={docsPageActive}
                  href="/dashboard/?create=true"
                  onClick={gaEvents.createNewApp}
                >
                  Create an App
                </CreateAppButton>
              )}
              {!!CTA.PrimaryHeaderCTA && <CTA.PrimaryHeaderCTA />}
            </>
          )
        : isMounted && (
            <>
              {showModalMenu && <GlobalStyle />}
              <MenuButton onClick={onMenuClick}>
                <MenuIcon isActive={showModalMenu} />
              </MenuButton>
              <CSSTransition in={showModalMenu} className="fadeScale" unmountOnExit timeout={200}>
                <ModalMenuContainer>
                  <ModalNavMenu
                    ctaKey={ctaKey}
                    titles={headerLinks.map((title) => ({ ...title, hasDropdown: false }))}
                  />
                </ModalMenuContainer>
              </CSSTransition>
            </>
          )}
    </NavContainer>
  )
}
